import React, { useEffect, useState } from 'react';
import {
  IonRow,
  IonCol,
  IonButton,
  IonLabel,
  IonText,
  IonSegment,
  IonSegmentButton,
  IonIcon,
} from '@ionic/react';
import { checkmarkOutline } from 'ionicons/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import PaystackPop from '@paystack/inline-js';
import {
  fetchBillingDetails,
  fetchUser,
  fetchSettings,
  mpesaStk,
  showLoading,
  initializePaystackTransaction,
  cancelCardSubscription,
  updateBillingPlan
} from '../../redux/actions';
import {
  check_empty_field,
  check_if_number_greater_than_100,
  check_if_positive_number,
  check_phone_number
} from '../../pages/Forms/validationChecks';
import { extractNumber } from '../../utils/numberFormat';
import { UNIFORM_RESOURCE_LOCATORS, colorTheme } from '../../utils/constants';

import MpesaPayment from './components/MpesaPayment';
import CreditCardPayment from './components/CreditCardPayment';
import PaybillPayment from './components/PaybillPayment';
import SubscriptionPlans from './components/SubscriptionPlans';
import SmsPlans from './components/SmsPlans';
import { Container } from 'reactstrap';

const PRICING_PLANS = {
  SUBSCRIPTION: 'SUB',
  SMS: 'SMS'
};

const SUBSCRIPTION_PERIODS = {
  ANNUAL: 'annual',
  QUARTERLY: 'quarterly',
  MONTHLY: 'monthly'
};

const PAYMENT_METHODS = {
  MPESA: 'Get MPESA Prompt',
  CREDIT_CARD: 'Credit Card',
  PAYBILL: 'Paybill'
};

const StepwisePricing = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { billing, user, paystack } = useSelector(state => ({
    billing: state.billing,
    user: state.user,
    paystack: state.paystack
  }));

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(props.paymentType === "sms" ? PRICING_PLANS.SMS : PRICING_PLANS.SUBSCRIPTION);
  const [subscriptionPeriod, setSubscriptionPeriod] = useState(SUBSCRIPTION_PERIODS.ANNUAL);
  const [smsAmount, setSmsAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.MPESA);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberFormatted, setPhoneNumberFormatted] = useState('');
  const [errors, setErrors] = useState({ smsAmount: '', phoneNumber: '', payment: '' });
  const [paybillDetails, setPaybillDetails] = useState({
    paybill: '',
    subscriptionAccountNumber: '',
    smsAccountNumber: '',
    monthlySubscriptionCost: 0,
    annualSubscriptionCost: 0,
    quarterlySubscriptionCost: 0,
    annualDiscountPercentage: 0,
    quarterlyDiscountPercentage: 0
  });

  useEffect(() => {
    dispatch(showLoading(true));
    dispatch(fetchBillingDetails());
    dispatch(fetchSettings());
    dispatch(fetchUser());
    dispatch(showLoading(false));
  }, []);

  useEffect(() => {
    if (user?.phoneNumber) {
      setPhoneNumber(user.phoneNumber);
    }
  }, [user]);

  useEffect(() => {
    if (paystack?.accessCode) {
      const paystackPopup = new PaystackPop();
      paystackPopup.resumeTransaction(paystack.accessCode);
    }
  }, [paystack?.accessCode]);

  useEffect(() => {
    if (billing) {
      setPaybillDetails({
        paybill: billing?.constants?.bomahut_mpesa_paybill || '',
        subscriptionAccountNumber: billing?.customerAccount?.accountNumberSubscription || '',
        smsAccountNumber: billing?.customerAccount?.accountNumberSms || '',
        monthlySubscriptionCost: billing?.customerAccount?.monthlySubscriptionCost || 0,
        annualSubscriptionCost: billing?.customerAccount?.annualSubscriptionCost || 0,
        quarterlySubscriptionCost: billing?.customerAccount?.quarterlySubscriptionCost || 0,
        annualDiscountPercentage: billing?.customerAccount?.annualDiscountPercentage || 0,
        quarterlyDiscountPercentage: billing?.customerAccount?.quarterlyDiscountPercentage || 0
      });
      setSubscriptionPeriod(billing?.customerAccount?.paymentSchedule || SUBSCRIPTION_PERIODS.ANNUAL);
    }
  }, [billing]);

  const getSelectedSubscriptionAmount = () => {
    if (subscriptionPeriod === SUBSCRIPTION_PERIODS.ANNUAL) {
      return paybillDetails.annualSubscriptionCost;
    }
    if (subscriptionPeriod === SUBSCRIPTION_PERIODS.QUARTERLY) {
      return paybillDetails.quarterlySubscriptionCost;
    }
    return paybillDetails.monthlySubscriptionCost;
  };

  const validateSmsAmount = value => {
    const error =
      check_empty_field(value) ||
      check_if_positive_number(value) ||
      check_if_number_greater_than_100(value);
    setErrors(prev => ({ ...prev, smsAmount: error }));
    return !error;
  };

  const validatePhoneNumber = value => {
    const error = check_empty_field(value) || check_phone_number(value);
    setErrors(prev => ({ ...prev, phoneNumber: error }));
    return !error;
  };

  const nextStep = () => {
    const oldSchedule = billing?.customerAccount?.paymentSchedule;
    if (currentStep === 1 && selectedPlan === PRICING_PLANS.SMS) {
      if (!validateSmsAmount(smsAmount)) return;
      setErrors(prev => ({ ...prev, smsAmount: '', payment: '' }));
      setCurrentStep(2);
    } else if (currentStep === 1 && selectedPlan === PRICING_PLANS.SUBSCRIPTION) {
      const newSchedule = subscriptionPeriod;
      if (oldSchedule === newSchedule) return setCurrentStep(2);

      dispatch(showLoading(true));
      dispatch(updateBillingPlan({ newSchedule }))
        .then(result => {
          if (result.payload?.success) {
            setErrors(prev => ({ ...prev, payment: '' }));
            setCurrentStep(2);
          }
        })
        .catch(console.error)
        .finally(() => dispatch(showLoading(false)));
    }
  };

  const handleStkPush = inputPhone => {
    if (!validatePhoneNumber(inputPhone)) return;
    const subscriptionAmount = extractNumber(getSelectedSubscriptionAmount());
    const amount = selectedPlan === PRICING_PLANS.SUBSCRIPTION
      ? subscriptionAmount
      : parseInt(smsAmount, 10);
    const phone = phoneNumberFormatted || inputPhone;

    dispatch(showLoading(true));
    dispatch(mpesaStk({ phoneNumber: phone, amount, paymentItem: selectedPlan }))
      .catch(console.error)
      .finally(() => dispatch(showLoading(false)));
  };

  const handleCreditCardPayment = () => {
    if (selectedPlan === PRICING_PLANS.SMS && !validateSmsAmount(smsAmount)) return;

    const subscriptionAmount = extractNumber(getSelectedSubscriptionAmount());
    const amount = selectedPlan === PRICING_PLANS.SUBSCRIPTION
      ? subscriptionAmount
      : parseInt(smsAmount, 10);

    const paymentItem = selectedPlan === PRICING_PLANS.SUBSCRIPTION ? 'subscription' : 'sms';

    dispatch(showLoading(true));
    dispatch(initializePaystackTransaction({ amount, paymentItem }))
      .catch(console.error)
      .finally(() => dispatch(showLoading(false)));
  };

  const handleCancelSubscription = () => {
    dispatch(showLoading(true));
    dispatch(cancelCardSubscription())
      .catch(console.error)
      .finally(() => dispatch(showLoading(false)));
  };

  const renderPlanSelector = () => (
    <>
      <IonSegment
        value={selectedPlan}
        onIonChange={(e) => setSelectedPlan(e.detail.value)}
      >
        <IonSegmentButton value={PRICING_PLANS.SUBSCRIPTION}>
          <IonLabel>Subscription</IonLabel>
        </IonSegmentButton>
        <IonSegmentButton value={PRICING_PLANS.SMS}>
          <IonLabel>Buy SMS</IonLabel>
        </IonSegmentButton>
      </IonSegment>

      {selectedPlan === PRICING_PLANS.SUBSCRIPTION ? (
        <SubscriptionPlans
          subscriptionPeriod={subscriptionPeriod}
          setSubscriptionPeriod={setSubscriptionPeriod}
          annualPrice={paybillDetails.annualSubscriptionCost}
          quarterlyPrice={paybillDetails.quarterlySubscriptionCost}
          monthlyPrice={paybillDetails.monthlySubscriptionCost}
          annualDiscountPercentage={paybillDetails.annualDiscountPercentage}
          quarterlyDiscountPercentage={paybillDetails.quarterlyDiscountPercentage}
        />
      ) : (
        <SmsPlans
          smsAmount={smsAmount}
          onSmsAmountChange={(e) => {
            const value = e.target.value;
            setSmsAmount(value);
            validateSmsAmount(value);
          }}
          errors={errors}
        />
      )}

      <IonRow className="ion-justify-content-center">
        <IonCol size="10">
          <IonButton expand="block" onClick={nextStep} color="primary">
            Next
          </IonButton>
        </IonCol>
      </IonRow>
    </>
  );

  const renderPaymentSection = () => {
    const subscriptionAmount = getSelectedSubscriptionAmount();
    const amount = selectedPlan === PRICING_PLANS.SUBSCRIPTION
      ? subscriptionAmount
      : parseInt(smsAmount);

    return (
      <>
        <IonText className="ion-text-center ion-margin-bottom">
          Choose  payment method
        </IonText>
        {Object.values(PAYMENT_METHODS).map((method) => (
          <div className="form-check my-3 ml-5" key={method}>
            <input
              className="form-check-input"
              type="radio"
              name="paymentMethod"
              id={`paymentMethod-${method}`}
              value={method}
              checked={paymentMethod === method}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            <label className="form-check-label" htmlFor={`paymentMethod-${method}`}>
              {method}
            </label>
          </div>
        ))}

        {paymentMethod === PAYMENT_METHODS.MPESA && (
          <MpesaPayment
            amount={amount}
            phoneNumber={phoneNumber}
            phoneNumberFormatted={phoneNumberFormatted}
            onPhoneNumberChange={(value, formatted) => {
              setPhoneNumber(value);
              setPhoneNumberFormatted(formatted);
            }}
            onPhoneNumberValidation={(value) => {
              const error = validatePhoneNumber(value);
              setErrors((prev) => ({ ...prev, phoneNumber: error }));
            }}
            onStkPush={handleStkPush}
            errors={errors}
          />
        )}
        {paymentMethod === PAYMENT_METHODS.CREDIT_CARD && (
          <CreditCardPayment
            amount={amount}
            onCreditCardPayment={handleCreditCardPayment}
            onCancelSubscription={handleCancelSubscription}
          />
        )}
        {paymentMethod === PAYMENT_METHODS.PAYBILL && (
          <PaybillPayment
            amount={amount}
            paybillDetails={paybillDetails}
            selectedPlan={selectedPlan}
          />
        )}

        <IonRow className="ion-justify-content-center ion-margin-top">
          <IonCol size="10">
            <IonButton expand="block" fill="outline" onClick={() => history.push(UNIFORM_RESOURCE_LOCATORS.billing)}>
              Done
            </IonButton>
            <IonButton expand="block" fill="outline" className="ion-margin-top" onClick={() => setCurrentStep(1)}>
              Previous
            </IonButton>
          </IonCol>
        </IonRow>
      </>
    );
  };

  return (
    <Container>
      <div>
        {currentStep === 1 && renderPlanSelector()}
        {currentStep === 2 && renderPaymentSection()}
      </div>
    </Container>
  );
};

export default StepwisePricing;
